import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Search,
  Eye,
  Trash2,
  Mail,
  Phone,
  Building,
  MessageSquare,
  CheckCircle,
  Clock,
  MailOpen
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;

const statusOptions = [
  { value: "all", label: "All Messages" },
  { value: "unread", label: "Unread" },
  { value: "read", label: "Read" },
  { value: "responded", label: "Responded" }
];

const getStatusColor = (status) => {
  const colors = {
    unread: "bg-yellow-100 text-yellow-800 border-yellow-200",
    read: "bg-blue-100 text-blue-800 border-blue-200",
    responded: "bg-green-100 text-green-800 border-green-200"
  };
  return colors[status] || "bg-slate-100 text-slate-800";
};

const getStatusIcon = (status) => {
  const icons = {
    unread: Clock,
    read: MailOpen,
    responded: CheckCircle
  };
  return icons[status] || Clock;
};

const AdminContacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedContact, setSelectedContact] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  useEffect(() => {
    fetchContacts();
  }, [statusFilter]);

  const fetchContacts = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      let url = `${API}/contacts`;
      if (statusFilter !== "all") {
        url += `?status=${statusFilter}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContacts(response.data);
    } catch (error) {
      console.error("Error fetching contacts:", error);
      toast.error("Failed to load contacts");
    } finally {
      setLoading(false);
    }
  };

  const handleViewContact = async (contact) => {
    setSelectedContact(contact);
    setIsViewOpen(true);
    
    // Mark as read if unread
    if (contact.status === "unread") {
      updateStatus(contact.id, "read");
    }
  };

  const updateStatus = async (contactId, newStatus) => {
    try {
      const token = localStorage.getItem("admin_token");
      await axios.put(`${API}/contacts/${contactId}/status?status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local state
      setContacts(contacts.map(c => 
        c.id === contactId ? { ...c, status: newStatus } : c
      ));
      
      if (selectedContact?.id === contactId) {
        setSelectedContact({ ...selectedContact, status: newStatus });
      }
      
      toast.success(`Message marked as ${newStatus}`);
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    }
  };

  const handleDelete = (contact) => {
    setSelectedContact(contact);
    setIsDeleteOpen(true);
  };

  const confirmDelete = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      await axios.delete(`${API}/contacts/${selectedContact.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Message deleted");
      fetchContacts();
      setIsViewOpen(false);
    } catch (error) {
      console.error("Error deleting contact:", error);
      toast.error("Failed to delete message");
    } finally {
      setIsDeleteOpen(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const filteredContacts = contacts.filter(contact =>
    contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.message.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="p-6 lg:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-48"></div>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-24 bg-slate-200 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Contact Messages</h1>
        <p className="text-slate-600 mt-1">View and manage contact form submissions</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Search messages..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-contacts-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[180px]" data-testid="status-filter">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            {statusOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Contacts List */}
      <div className="space-y-4">
        {filteredContacts.length === 0 ? (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-12 text-center">
              <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No messages found</p>
            </CardContent>
          </Card>
        ) : (
          filteredContacts.map((contact, index) => {
            const StatusIcon = getStatusIcon(contact.status);
            return (
              <motion.div
                key={contact.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className={`border-0 shadow-sm hover:shadow-md transition-shadow ${contact.status === "unread" ? "border-l-4 border-l-yellow-500" : ""}`}>
                  <CardContent className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                      <div className="flex items-start gap-4 flex-1">
                        <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-white font-semibold text-lg">
                            {contact.name?.charAt(0) || "?"}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-lg font-semibold text-slate-900">{contact.name}</h3>
                            <Badge className={`text-xs ${getStatusColor(contact.status)}`}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {contact.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-500">{contact.email}</p>
                          {contact.company && (
                            <p className="text-sm text-slate-400">{contact.company}</p>
                          )}
                          <p className="text-slate-600 mt-2 line-clamp-2">{contact.message}</p>
                          <p className="text-xs text-slate-400 mt-2">
                            {formatDate(contact.created_at)}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleViewContact(contact)}
                          data-testid={`view-contact-${contact.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" /> View
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleDelete(contact)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-contact-${contact.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })
        )}
      </div>

      {/* View Contact Modal */}
      <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Message Details</DialogTitle>
            <DialogDescription>
              Contact form submission from {selectedContact?.name}
            </DialogDescription>
          </DialogHeader>

          {selectedContact && (
            <div className="space-y-6 py-4">
              {/* Sender Info */}
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-2xl">
                    {selectedContact.name?.charAt(0)}
                  </span>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-slate-900">{selectedContact.name}</h3>
                  {selectedContact.company && (
                    <p className="text-slate-500">{selectedContact.company}</p>
                  )}
                  <Badge className={`mt-2 ${getStatusColor(selectedContact.status)}`}>
                    {selectedContact.status}
                  </Badge>
                </div>
              </div>

              {/* Contact Info */}
              <div className="grid sm:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-slate-400" />
                  <a href={`mailto:${selectedContact.email}`} className="text-[#1e3a8a] hover:underline">
                    {selectedContact.email}
                  </a>
                </div>
                {selectedContact.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <a href={`tel:${selectedContact.phone}`} className="text-[#1e3a8a] hover:underline">
                      {selectedContact.phone}
                    </a>
                  </div>
                )}
                {selectedContact.company && (
                  <div className="flex items-center gap-2">
                    <Building className="w-4 h-4 text-slate-400" />
                    <span>{selectedContact.company}</span>
                  </div>
                )}
              </div>

              {/* Message */}
              <div>
                <Label className="text-slate-700 font-medium">Message</Label>
                <div className="mt-2 p-4 bg-slate-50 rounded-lg text-slate-600 whitespace-pre-wrap">
                  {selectedContact.message}
                </div>
              </div>

              {/* Status Actions */}
              <div>
                <Label className="text-slate-700 font-medium mb-3 block">Update Status</Label>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => updateStatus(selectedContact.id, "read")}
                    className={selectedContact.status === "read" ? "border-blue-500 bg-blue-50" : ""}
                    data-testid="status-read"
                  >
                    <MailOpen className="w-4 h-4 mr-1" /> Mark as Read
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => updateStatus(selectedContact.id, "responded")}
                    className={selectedContact.status === "responded" ? "bg-green-600" : "bg-[#1e3a8a] hover:bg-[#172554]"}
                    data-testid="status-responded"
                  >
                    <CheckCircle className="w-4 h-4 mr-1" /> Mark as Responded
                  </Button>
                </div>
              </div>

              {/* Quick Reply */}
              <div className="pt-4 border-t">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => window.location.href = `mailto:${selectedContact.email}?subject=Re: Your inquiry to Omnilink Solutions`}
                  data-testid="reply-email"
                >
                  <Mail className="w-4 h-4 mr-2" /> Reply via Email
                </Button>
              </div>

              <p className="text-xs text-slate-400">
                Received on {formatDate(selectedContact.created_at)}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Message?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the message from {selectedContact?.name}. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-contact"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminContacts;
